import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp, Calendar, DollarSign, FileText, MapPin, CheckCircle, Clock } from "lucide-react";
import { VisaGuideData, VisaRequirement } from "@/types/visa";

interface VisaRequirementsProps {
  visaData: VisaGuideData[string][string];
  originData?: VisaGuideData[string][string]["originSpecific"][string];
  originCountries: Array<{ value: string; label: string; }>;
  travelingFrom?: string;
  expandedRequirements: number[];
  onToggleRequirement: (index: number) => void;
}

const VisaRequirements = ({
  visaData,
  originData,
  originCountries,
  travelingFrom,
  expandedRequirements,
  onToggleRequirement,
}: VisaRequirementsProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">Visa Requirements</CardTitle>
        <CardDescription>Documents and conditions required for this visa</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {visaData.requirements.map((req, index) => (
            <RequirementItem
              key={index}
              requirement={req}
              index={index}
              isExpanded={expandedRequirements.includes(index)}
              onToggle={() => onToggleRequirement(index)}
            />
          ))}
          
          {originData && originData.additionalRequirements && (
            <>
              <h3 className="font-semibold text-lg mt-6 mb-3">
                Additional Requirements for {originCountries.find(c => c.value === travelingFrom)?.label.split(' ')[0]} Citizens:
              </h3>
              {originData.additionalRequirements.map((req, index) => (
                <RequirementItem
                  key={`origin-${index}`}
                  requirement={req}
                  index={index + 100}
                  isExpanded={expandedRequirements.includes(index + 100)}
                  onToggle={() => onToggleRequirement(index + 100)}
                  isOriginSpecific
                />
              ))}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

interface RequirementItemProps {
  requirement: VisaRequirement;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  isOriginSpecific?: boolean;
}

const RequirementItem = ({
  requirement,
  isExpanded,
  onToggle,
  isOriginSpecific
}: RequirementItemProps) => {
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case "FileText":
        return <FileText className="h-5 w-5" />;
      case "Calendar":
        return <Calendar className="h-5 w-5" />;
      case "DollarSign":
        return <DollarSign className="h-5 w-5" />;
      case "MapPin":
        return <MapPin className="h-5 w-5" />;
      case "CheckCircle":
        return <CheckCircle className="h-5 w-5" />;
      case "Clock":
        return <Clock className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  const getDetailedInfo = (title: string) => {
    switch(title) {
      case "Valid Passport":
        return "Your passport must be valid for at least six months beyond your intended period of stay in the United States. It should have at least one blank page for visa stamps. If your passport expires in less than six months, you must renew it before applying for your visa.";
      case "Form I-20":
        return "The Form I-20 is issued by your SEVP-approved school after you've been accepted. It must be signed by both the school's designated official and yourself. This document contains important information about your academic program, start date, and estimated costs. Keep this document safe as you'll need it throughout your stay in the US.";
      case "Visa Application (DS-160)":
        return "The DS-160 is the Online Nonimmigrant Visa Application form. All fields must be completed in English using English-language characters. The form typically takes 45-60 minutes to complete. After submission, you must print the confirmation page to bring to your visa interview.";
      case "Financial Documents":
        return "You must provide evidence that you have sufficient funds to cover your tuition and living expenses during your intended period of study. This can include bank statements, scholarship letters, sponsorship documents, or other proof of financial support. Documents should be recent (less than 3 months old).";
      case "Academic Documents":
        return "Prepare your academic transcripts, standardized test scores (such as TOEFL, IELTS, SAT, GRE, or GMAT), and certificates from your previous educational institutions. These documents should be translated into English if they're in another language.";
      case "Photographs":
        return "Two recent passport-style color photographs meeting the US visa photo requirements: 2x2 inches (51x51 mm) in size, taken within the last 6 months, showing full face against a white or off-white background. Digital modifications are not acceptable.";
      default:
        return requirement.details || "Additional information will be provided during the application process.";
    }
  };

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <CollapsibleTrigger 
        className={`flex items-center justify-between w-full p-4 border rounded-lg hover:bg-gray-50 transition-colors ${
          isOriginSpecific ? 'bg-blue-50' : ''
        }`}
      >
        <div className="flex items-center">
          <div className={`mr-3 ${requirement.isRequired ? 'text-red-500' : 'text-gray-400'}`}>
            {renderIcon(requirement.icon)}
          </div>
          <div className="text-left">
            <h4 className="font-medium">{requirement.title}</h4>
            <p className="text-sm text-gray-500">{requirement.description}</p>
          </div>
        </div>
        <div className="flex items-center">
          {requirement.isRequired && (
            <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded mr-2">Required</span>
          )}
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent 
        className={`mt-2 p-4 border border-t-0 rounded-b-lg ${
          isOriginSpecific ? 'bg-blue-50' : 'bg-gray-50'
        }`}
      >
        <p className="text-gray-700 leading-relaxed">{getDetailedInfo(requirement.title)}</p>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default VisaRequirements;
